import {
  type Collection,
  List as ImmutableList,
  Map as ImmutableMap,
  Record as ImmutableRecord,
  Set as ImmutableSet,
  type List,
  type MapOf,
  type RecordOf,
} from 'immutable';
import uniqBy from 'lodash/uniqBy';
import { DateTime, Interval } from 'luxon';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  constructPredefinedInterval,
  getStartOfMonthFromThisMonth,
  intervalContainsInclusive,
} from 'app-components/utils/date';

import { intervalContainsInclusiveISO } from '../../utils/date/intervalContainsInclusive';
import { accountNodeUtils } from '../account-nodes';
import { nodeAssetIds, nodeDebtIds } from '../account-nodes/accountNodeUtils';
import { accountsSelectors, accountsUtils } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import { accountsBalancesSelectors } from '../accounts-balances';
import { categoriesSelectors } from '../categories';
import { selectorsHelpers } from '../core';
import { goalsSelectors } from '../goals';
import { tagsSelectors } from '../tags';
import { transactionListUtils } from '../transaction-list';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from '../transactions';
import { isZeroAmountOverflowSplit } from '../transactions/splitsHelpers';
import type { SplitItemProps, TransactionItem, TransactionRecord } from '../transactions/transactionsTypes';
import type {
  AccountBalanceProps,
  AccountBalancesResult,
  BalancePeriod,
  BalanceReportProps,
  BalanceReportState,
  CategoriesById,
  ChartPeriod,
  DisplayReportResult,
  HeaderData,
  IncomeSpendingDisplayData,
  IncomeSpendingDisplayResult,
  MapOfProps,
  MonthlyGoal,
  MonthlySummaryData,
  MonthlySummaryState,
  NetIncomeDisplayResult,
  NetWorthAccount,
  NetWorthDisplayResult,
  NetWorthReportData,
  OverTimeDataResult,
  ReportDataResult,
  ReportFilteredState,
  ReportFilterProps,
  ReportGroupingBase,
  ReportGroupTotal,
  ReportPeriod,
  ReportPeriodGroup,
  ReportProps,
  ReportSpecificFilter,
  ReportState,
  ReportTimeStep,
  ReportTotalTransactions,
  ReportTxnItemList,
  ReportTxnList,
  SavingsDisplayData,
  SavingsDisplayResult,
  SpendingHistoryProps,
  SpendingHistoryResult,
  SubDataItem,
  TagsById,
  TotalDataResult,
  TransactionChartPeriod,
} from './reportsTypes';
import {
  adjustDateRangeEndToToday,
  capGrouping,
  constructDatePeriods,
  convertAndSortGrouping,
  getGroupingKeys,
  getReportSpecificFilterFromReportName,
  getTimestepFormatting,
  makeGroupByPlural,
} from './reportsUtils';
import { getTransactionsByAccountForReport } from './reportTransactionSelectors';

// getTotalData fills these with arrays before convertAndSortGrouping sorts them.
type TotalDataSubgroup = Omit<ReportGroupingBase, 'txns' | 'txnItems'> & {
  txns: TransactionRecord[];
  txnItems: TransactionItem[];
};
type TotalDataGroup = TotalDataSubgroup & { subgroups: Record<string, TotalDataSubgroup> };
// Fields read from accountsBalancesSelectors.getSimplifiedBalancesByAccount.
type ReportAccountBalances = {
  id?: string | null;
  accountBalances: { balanceOn: string; balanceAmount?: number | null }[];
};

const { getL2GroupingData } = accountNodeUtils;
const getReportableAccounts: (state: ReportFilteredState) => Collection<string | number, Pick<Account, 'id' | 'type'>> =
  selectorsHelpers.createTrimSelector(accountsSelectors.getAccountsById, (account) => ({
    id: account.id,
    type: account.type,
  }));

// The shared filter preserves the input collection; an array input always produces an array.
function matchesAppliedFilters(
  filter: NonNullable<ReturnType<typeof transactionListUtils.createFilterFromFilterObject>>,
  transaction: TransactionRecord,
): boolean {
  const filtered = filter([transaction]);
  return Array.isArray(filtered) && filtered.length > 0;
}

// local selector used to filter txns by dateRange (luxon Interval) and a filterObject
export const getReportFilteredTxns = createSelector(
  (state: ReportFilteredState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state),
  getReportableAccounts,
  getTransactionsByAccountForReport,
  (state: ReportFilteredState) => categoriesSelectors.getCategoriesById(state),
  (_state: ReportFilteredState, props: ReportFilterProps) =>
    transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (_state: ReportFilteredState, props: ReportFilterProps) => props.reportSpecificFilter, // this is for stuff like spending or income, function that accepts txn, true for keep
  (_state: ReportFilteredState, props: ReportFilterProps) => props.upcomingTxnsIncluded,
  (_state: ReportFilteredState, props: ReportFilterProps) => props.dateRange?.start?.toISODate(),
  (_state: ReportFilteredState, props: ReportFilterProps) => props.dateRange?.end?.toISODate(),
  (
    loadPending,
    reportableAccounts,
    txnsById,
    categoriesById,
    filterFn,
    reportSpecificFilter,
    upcomingTxnsIncluded = false,
    dateRangeStartISO,
    dateRangeEndISO,
  ): ReportTxnList | null => {
    if (loadPending) {
      return null;
    }

    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'report-transactions',
      {
        reportableAccounts,
        txnsById,
        categoriesById,
        filterFn,
        dateRangeEndISO,
        dateRangeStartISO,
        reportSpecificFilter,
        upcomingTxnsIncluded,
      },
      20,
      selectorsHelpers.memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );
    return selectorsHelpers.memoizeWithKey(
      'report-transactions',
      (): ReportTxnList | null => {
        // this is really where the selector starts
        // combine txns from reportable accounts
        const allTxns = ImmutableList<TransactionRecord>().withMutations((boi) => {
          reportableAccounts
            .filter((a) => !accountsUtils.isLoanAccount(a))
            .forEach((acc) => {
              txnsById
                .get(String(acc.id))
                ?.toList()
                .filter(
                  (txn) =>
                    txn.type !== 'INVESTMENT' || // drop any INVESTMENT non MISCELLANEOUS
                    [
                      transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS,
                      transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS,
                    ].some((type) => type === txn.investmentTxnType),
                )
                .forEach((txn) => boi.push(txn));
            });
        });

        const withoutUpcomingTxns = !upcomingTxnsIncluded
          ? allTxns.filter((txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING')
          : allTxns;

        // filter txns out by their date and reportSpecificFilter
        const dateFilteredReportSpecificTxns =
          dateRangeStartISO || dateRangeEndISO
            ? withoutUpcomingTxns.filter(
                (txn) =>
                  (!reportSpecificFilter || reportSpecificFilter(txn, categoriesById)) &&
                  intervalContainsInclusiveISO(dateRangeStartISO, dateRangeEndISO, txn.postedOn),
              )
            : withoutUpcomingTxns;

        // filter out txns by the filter object if given
        return filterFn ? (filterFn(dateFilteredReportSpecificTxns) as ReportTxnList) : dateFilteredReportSpecificTxns;
      },
      cacheKey,
      20,
    );
  },
);
// note - this doesn't account for splits. Not being used at this moment, but will be used once I have implemented splits total filtering - Panelli
export const getFilteredDataOverTime = createSelector(
  (state: ReportState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state),
  (state: ReportState, props: SpendingHistoryProps) =>
    getReportFilteredTxns(state, {
      ...props,
      reportSpecificFilter: getReportSpecificFilterFromReportName(props.reportName),
    }),
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (_state: ReportState, props: SpendingHistoryProps) => props.dateRange,
  (_state: ReportState, props: SpendingHistoryProps) => props.filterObject,
  (_state: ReportState, props: SpendingHistoryProps) =>
    transactionListUtils.createFilterFromFilterObject(props.filterObject),
  () => getReportSpecificFilterFromReportName('total-spending'),
  (
    loadPending,
    transactions,
    categoriesById,
    tagsById,
    dateRange,
    filterObject,
    filterObjectFn,
    reportSpecificFilter,
  ): SpendingHistoryResult | null => {
    if (loadPending) return null;
    const dateRangeString = JSON.stringify(dateRange);
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'filtered-data-over-time',
      { transactions, dateRangeString, filterObjectFn },
      5,
      selectorsHelpers.memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );
    return selectorsHelpers.memoizeWithKey(
      'filtered-data-over-time',
      (): SpendingHistoryResult | null => {
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn: TransactionRecord, cbid: CategoriesById) =>
              reportSpecificFilter(txn, cbid) && matchesAppliedFilters(filterObjectFn, txn)
          : reportSpecificFilter;
        const overTimeData = getOverTimeData(
          transactions,
          dateRange ?? constructPredefinedInterval({ value: 'RECENT_6_MONTHS' }, DateTime.local()),
          'months',
          'categories',
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        const periods = overTimeData?.get('periods');
        let activeMonths = 0;
        let sum = 0;
        periods?.forEach((period) => {
          if (period.total) activeMonths += 1;
          sum += period.total;
        });
        const chartData = periods?.map((period) => {
          const time = DateTime.fromISO(period.timeStamp);
          return {
            month: time.monthShort,
            totalAmount: -1 * period.total,
            totalAmountString: `$${
              -1 * period.total >= 1000 ? ((-1 * period.total) / 1000).toFixed(1) : Math.round(-1 * period.total)
            }${-1 * period.total > 1000 ? 'k' : ''}`,
            currentAmount: -1 * period.total,
          };
        });
        if (activeMonths < 1) activeMonths = 1;
        return { periods: chartData?.toArray(), average: Math.round(sum / activeMonths) };
      },
      cacheKey,
      5,
    );
  },
);

type ReportSplit = RecordOf<SplitItemProps> | TransactionRecord;
type ReportSplitEntry = { split: ReportSplit; view?: TransactionRecord; item?: TransactionItem };

// A split's filter view and transaction item depend only on its immutable transaction record, so reports
// rebuild them only for edited records. Entries are positional because a split list can repeat a record.
const reportSplitEntries = new WeakMap<TransactionRecord, ReportSplitEntry[]>();

// Only recent records are cached: month-to-date reports rerun on every edit, and caching older ones
// would keep a copy of every reported transaction for the whole session.
const RECENT_CACHE_DAYS = 62;
const getRecentCacheStart = (): string => DateTime.local().minus({ days: RECENT_CACHE_DAYS }).toISODate() ?? '';

const createReportSplitEntries = (txn: TransactionRecord): ReportSplitEntry[] => {
  const splitItems = txn.split?.items;
  return splitItems && !Array.isArray(splitItems) && splitItems.size > 0
    ? splitItems.toArray().map((split) => ({ split }))
    : [{ split: txn }];
};

function getReportSplitEntries(txn: TransactionRecord, cacheStart: string): ReportSplitEntry[] {
  // Plain objects can change in place, so only immutable records are cached.
  if (!ImmutableRecord.isRecord(txn) || (txn.postedOn ?? '') < cacheStart) return createReportSplitEntries(txn);
  let entries = reportSplitEntries.get(txn);
  if (!entries) {
    entries = createReportSplitEntries(txn);
    if (entries.every(({ split }) => ImmutableRecord.isRecord(split))) reportSplitEntries.set(txn, entries);
  }
  return entries;
}

// purely for checking the split, we merge to check payee and account
function getReportSplitView(txn: TransactionRecord, entry: ReportSplitEntry): TransactionRecord {
  entry.view ??= txn.merge(entry.split).set('split', undefined);
  return entry.view;
}

function getReportTransactionItem(txn: TransactionRecord, entry: ReportSplitEntry): TransactionItem {
  if (!entry.item) {
    const splitItem = entry.split.id !== txn.id ? (entry.split as RecordOf<SplitItemProps>) : undefined;
    entry.item = transactionsTypes.mkTransactionItem({
      transaction: txn,
      splitItem,
      item: {
        ...txn.toObject(),
        ...(ImmutableRecord.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
      },
    });
  }
  return entry.item;
}

// timeStep is days || months || years
function calculateOverTimeData(
  txns: ReportTotalTransactions | null | undefined,
  dateRange: Interval,
  timeStep: string | undefined,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns: boolean,
  totalOnly: true,
): number;
function calculateOverTimeData(
  txns: ReportTotalTransactions | null | undefined,
  dateRange: Interval,
  timeStep: string | undefined,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns: boolean,
  totalOnly?: false,
): OverTimeDataResult;
function calculateOverTimeData(
  txns: ReportTotalTransactions | null | undefined,
  dateRange: Interval,
  timeStep: string | undefined,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns = false,
  totalOnly = false,
): number | OverTimeDataResult {
  const periods = constructDatePeriods(dateRange, timeStep).map((period) => ({
    ...period,
    total: 0,
    ...(!totalOnly
      ? {
          txns: [] as TransactionRecord[],
          txnItems: [] as TransactionItem[],
          ...(groupBy ? { groups: {} as Record<string, ReportPeriodGroup> } : {}),
        }
      : {}),
  }));

  const allGroups: Record<string, ReportGroupTotal> = {};
  const allTxns: TransactionRecord[] = [];
  const allTxnItems: TransactionItem[] = [];
  let total = 0;

  const dateStartISO = dateRange?.start?.toISODate();
  const dateEndISO = dateRange?.end?.toISODate();
  const cacheStart = getRecentCacheStart();

  txns?.forEach((txn) => {
    let insertedTxn = false; // to avoid duplicating txns when split
    const txnDate = txn.postedOn || txn.stDueOn;
    // The date check is the same for every split, so skip out-of-range transactions before reading splits.
    if (!txnDate || !intervalContainsInclusiveISO(dateStartISO, dateEndISO, txnDate)) return;
    getReportSplitEntries(txn, cacheStart).forEach((entry) => {
      const { split } = entry;
      if (isZeroAmountOverflowSplit(split)) return;
      if (!reportSpecificFilter(getReportSplitView(txn, entry), categoriesById)) {
        return;
      }
      const period = periods.find(({ isoMask }) => txnDate.startsWith(isoMask)); // obtain period object

      if (!period) {
        return;
      }

      const splitExcluded = (txn.isExcludedFromReports || split.isExcludedFromReports) && !includeAllTxns;
      const amount = Number.parseFloat(String(split.amount));

      if (!splitExcluded) {
        // Both summaries and full reports use the same accepted splits and amount arithmetic.
        period.total += amount;
        total += amount;
      }
      if (totalOnly) return;

      const txnItem = getReportTransactionItem(txn, entry);

      if (!groupBy) {
        // no grouping just plain
        if (!insertedTxn) {
          period.txns!.push(txn);
          allTxns.push(txn);
          insertedTxn = true;
        }
        period.txnItems!.push(txnItem);
        allTxnItems.push(txnItem);
      } else if (groupBy) {
        const { groupKeys } = getGroupingKeys(txn, split, groupBy, categoriesById, tagsById);
        const groupKey = String(groupKeys[0]);
        if (period.groups![groupKey]) {
          // group has been created
          if (!splitExcluded) {
            period.groups![groupKey].total += amount;
            period.groups![groupKey].isIgnored = false;
          }
          if (!insertedTxn) {
            period.groups![groupKey].txns!.push(txn);
            period.txns!.push(txn);
            allTxns.push(txn);
            insertedTxn = true;
          }
          period.groups![groupKey].txnItems!.push(txnItem);
          period.txnItems!.push(txnItem);
          allTxnItems.push(txnItem);
        } else {
          // group not created yet
          period.groups![groupKey] = {
            isIgnored: splitExcluded,
            txns: [txn],
            txnItems: [txnItem],
            total: !splitExcluded ? amount : 0,
          };
          if (!insertedTxn) {
            period.txns!.push(txn);
            allTxns.push(txn);
            insertedTxn = true;
          }
          period.txnItems!.push(txnItem);
          allTxnItems.push(txnItem);
        }
        allGroups[groupKey] = {
          total: (allGroups[groupKey]?.total || 0) + (!splitExcluded ? amount : 0),
          // If any period has a group key that is not ignored, then the allGroups
          // copy of that group key should also not be ignored. Only if the group
          // is ignored from all periods should we set this to true. If even one
          // period has isIgnored set to false, then we leave the all groups isIgnored flag false.
          isIgnored: allGroups[groupKey]?.isIgnored === false ? false : period.groups![groupKey].isIgnored,
        };
      }
    });
  });

  if (totalOnly) return total;

  return ImmutableMap<MapOfProps<OverTimeDataResult>>({
    total,
    allTxns: ImmutableList(allTxns),
    allTxnItems: ImmutableList(allTxnItems),
    // The total-only path returned above; every remaining period has detail arrays.
    periods: periods as List<ReportPeriod>,
    allGroups,
  });
}

export const getOverTimeData = (
  txns: ReportTotalTransactions | null | undefined,
  dateRange: Interval,
  timeStep: string | undefined,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns = false,
): OverTimeDataResult =>
  calculateOverTimeData(
    txns,
    dateRange,
    timeStep,
    groupBy,
    categoriesById,
    tagsById,
    reportSpecificFilter,
    includeAllTxns,
  );

export const getOverTimeTotal = (
  txns: ReportTotalTransactions | null | undefined,
  dateRange: Interval,
  timeStep: ReportTimeStep,
  categoriesById: CategoriesById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns = false,
): number =>
  calculateOverTimeData(
    txns,
    dateRange,
    timeStep,
    null,
    categoriesById,
    ImmutableMap(),
    reportSpecificFilter,
    includeAllTxns,
    true,
  );

// if getting summed data must have a groupBy that's not By Total
export const getTotalData = (
  txns: ReportTotalTransactions,
  dateRange: Interval,
  timeStep: string | undefined,
  groupBy: string | null | undefined,
  categoriesById: CategoriesById,
  tagsById: TagsById,
  reportSpecificFilter: ReportSpecificFilter,
  includeAllTxns = false,
): TotalDataResult => {
  // const start = performance.now();
  const groups: Record<string, TotalDataGroup> = {};
  let allTotal = 0;

  const allTxns: TransactionRecord[] = [];
  const allTxnItems: TransactionItem[] = [];
  const cacheStart = getRecentCacheStart();
  txns.forEach((txn) => {
    // we have to keep track of which groups the txn has been added to to avoid duplucating in the same group/subgroup but not different ones, bleh
    const groupsWithTxn: Array<string | undefined> = [];
    let insertedTxn = false;

    getReportSplitEntries(txn, cacheStart).forEach((entry) => {
      const { split } = entry;
      if (isZeroAmountOverflowSplit(split)) return;
      if (!reportSpecificFilter(getReportSplitView(txn, entry), categoriesById)) {
        return;
      }

      const txnItem = getReportTransactionItem(txn, entry);
      if (!insertedTxn) {
        allTxns.push(txn);
        insertedTxn = true;
      }
      allTxnItems.push(txnItem);

      const { groupKeys, subKey, parentCategoryId, subCategoryId } = getGroupingKeys(
        txn,
        split,
        groupBy,
        categoriesById,
        tagsById,
      );
      const splitExcluded = (txn.isExcludedFromReports || split.isExcludedFromReports) && !includeAllTxns;
      const amount = Number.parseFloat(String(split.amount));
      let alreadyInAllTotal = false;
      groupKeys.forEach((groupName) => {
        const groupKey = String(groupName);
        if (groups[groupKey]) {
          // group exists
          if (!groupsWithTxn.includes(groupName)) {
            groupsWithTxn.push(groupName);
            groups[groupKey].txns.push(txn);
          }
          groups[groupKey].txnItems.push(txnItem);

          if (!splitExcluded) {
            groups[groupKey].total += amount;
            allTotal += alreadyInAllTotal ? 0 : amount;
            alreadyInAllTotal = true;
            groups[groupKey].isIgnored = false;
          }

          if (subKey) {
            // if it has a subkey then put it in its subgroup
            if (!groups[groupKey].subgroups[subKey]) {
              // if the subgroup hasn't been created yet
              groups[groupKey].subgroups[subKey] = {
                total: 0,
                isIgnored: splitExcluded,
                txns: [],
                txnItems: [],
                groupKey: subKey,
                categoryId: subCategoryId,
              };
            }

            if (!splitExcluded) {
              groups[groupKey].subgroups[subKey].isIgnored = false;
            }
            groups[groupKey].subgroups[subKey].total += !splitExcluded ? amount : 0;

            if (!groupsWithTxn.includes(groupKey + subKey)) {
              groupsWithTxn.push(groupKey + subKey);
              groups[groupKey].subgroups[subKey].txns.push(txn);
            }
            groups[groupKey].subgroups[subKey].txnItems.push(txnItem);
          }
        } else {
          // group does not exist
          groups[groupKey] = {
            groupKey: groupName,
            isIgnored: splitExcluded,
            categoryId: parentCategoryId,
            txns: [txn],
            txnItems: [txnItem],
            total: !splitExcluded ? amount : 0,
            subgroups: subKey
              ? {
                  [subKey]: {
                    total: !splitExcluded ? amount : 0,
                    isIgnored: splitExcluded,
                    txns: groupsWithTxn.includes(groupKey + subKey) ? [] : [txn],
                    txnItems: [txnItem],
                    groupKey: subKey,
                    categoryId: subCategoryId,
                  },
                }
              : {},
          };
          groupsWithTxn.push(groupKey + (subKey || ''));
          if (!alreadyInAllTotal) {
            allTotal += !splitExcluded ? amount : 0;
            alreadyInAllTotal = true;
          }
        }
      });
    });
  });

  return ImmutableMap<MapOfProps<TotalDataResult>>({
    caller: 'getTotalData',
    allTxns: ImmutableList(allTxns),
    allTxnItems: ImmutableList(allTxnItems),
    groups,
    total: allTotal,
  });
};

const filterAccounts = (
  accountsById: ImmutableMap<string, Account>,
  reportableAccounts: ImmutableMap<string, Account>,
  filterObject: BalanceReportProps['filterObject'],
): ImmutableMap<string, Account> =>
  accountsById?.filter(
    (
      acc,
      accountId, // at this time the only filter allowed for Savings and NetWorth report are accounts
    ) =>
      reportableAccounts.has(accountId) && // account is reportable
      // only one of the following lines has to be true for the accountId to be kept
      (!filterObject?.get('accounts') ||
        filterObject.get('accounts')?.isEmpty() || // there's no filter or no filters for accounts, or the filter for accounts is empty
        (!filterObject?.get('filterOut') && filterObject?.get('accounts')?.includes(accountId)) || // filtering in and it's included in accounts
        (filterObject?.get('filterOut') && !filterObject?.get('accounts')?.includes(accountId))),
  ); // filtering out and it's not included in accounts'

export const getAccountBalances = createSelector(
  (state: BalanceReportState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state: BalanceReportState) => {
    const accounts = accountsSelectors.getAccountsById(state);
    const excludeBalance = featureFlagsSelectors.getFeatureFlag(state, 'excludeAccountBalanceFromReports');
    if (excludeBalance) {
      return accounts.filter((account) => !account.isExcludedFromReports);
    }
    return accounts;
  },
  (state: BalanceReportState) => transactionsSelectors.getTransactionsByAccountId(state),
  (_state: BalanceReportState, props: AccountBalanceProps) => props.accountsById,
  (_state: BalanceReportState, props: AccountBalanceProps) => props.dateRange.toISO(),
  (_state: BalanceReportState, props: AccountBalanceProps) => props.timeStep,
  (_state: BalanceReportState, props: AccountBalanceProps) => props.filterObject,
  (_state: BalanceReportState, props: AccountBalanceProps) => getReportSpecificFilterFromReportName(props.reportName),
  (state: BalanceReportState): List<ReportAccountBalances> =>
    accountsBalancesSelectors.getSimplifiedBalancesByAccount(state, accountsSelectors.getAccountsById(state)),
  (
    loadPending,
    reportableAccounts,
    txnsByAccountId,
    accountsById,
    dateRangeISO,
    timeStep,
    filterObject,
    reportSpecificFilter,
    investmentAndLoanBalances,
  ): AccountBalancesResult | null => {
    if (loadPending) {
      return null;
    }
    const dateRange = Interval.fromISO(dateRangeISO);
    const accountsToCalculate = filterAccounts(accountsById, reportableAccounts, filterObject);

    const balancePeriods: List<BalancePeriod> = constructDatePeriods(dateRange, timeStep);
    const periodsByAcctId: List<BalancePeriod> = constructDatePeriods(dateRange, timeStep);
    let allTxns = ImmutableList<TransactionRecord>();
    let allTxnItems = ImmutableList<TransactionItem>();
    const retTxnsByAccountId: Record<string, ReportTxnList> = {};
    const txnItemsByAccountId: Record<string, ReportTxnItemList> = {};

    const timeStepWithoutS = getTimestepFormatting(timeStep);
    // Legacy balance-report Maps select accounts through .get() but skip the Record-only transaction filter.
    const transactionFilter =
      filterObject && !ImmutableMap.isMap(filterObject)
        ? transactionsUtils.createTransactionsFilterByObjFn(filterObject)
        : null;
    const filterIncludingAppliedFilter = transactionFilter
      ? (txn: TransactionRecord, cbid: CategoriesById) => reportSpecificFilter(txn, cbid) && transactionFilter(txn)
      : reportSpecificFilter;

    accountsToCalculate.forEach((acc, accountId) => {
      // First, we get all the txns and retTxnsByAcctId
      const normalizedName = acc.name!.replaceAll(/\(|\)/gm, ' '); // SIMPL-22712
      const txns = txnsByAccountId.get(accountId)!.toList();
      // we calculate the data up until today
      const overTimeData = getOverTimeData(
        txns,
        adjustDateRangeEndToToday(dateRange),
        timeStep,
        null,
        ImmutableMap(),
        ImmutableMap(),
        filterIncludingAppliedFilter,
        true,
      );
      // but then we remove the txns outside the range
      const dateCorrectedTxns = overTimeData
        .get('allTxns')
        .filter((txn) => intervalContainsInclusive(dateRange, DateTime.fromISO(txn.postedOn)));
      allTxns = allTxns.concat(dateCorrectedTxns);
      retTxnsByAccountId[normalizedName] = dateCorrectedTxns;

      const transactionIds = ImmutableSet(dateCorrectedTxns.map((txn) => txn.id));
      const dateCorrectedTxnItems = overTimeData
        .get('allTxnItems')
        .filter((txnItem) => transactionIds.has(txnItem.transaction.id))
        .toList();
      allTxnItems = allTxnItems.concat(dateCorrectedTxnItems);
      txnItemsByAccountId[normalizedName] = dateCorrectedTxnItems;

      // We grab the total balances of the accounts sorted by day
      const balancesByDay = investmentAndLoanBalances.find((myAcc) => myAcc.id === accountId)?.accountBalances;

      // Get the periods we will cycle through
      const periods = constructDatePeriods(dateRange, timeStep);

      let periodIndex = balancePeriods.size - 1;
      periods.reverse().forEach((period) => {
        if (intervalContainsInclusive(dateRange, DateTime.fromISO(period.timeStamp).startOf('day'))) {
          // Sanity test

          // The date we will use for the requested timestep
          let periodTimeStamp = DateTime.fromISO(period.timeStamp).toISODate();

          //  If it is the last period we are going through, set the date to the END of the timestep so we get the FINAL VALUE of the month
          //  This is ONLY used for balances values because in a monthly timestep, we want the value at the END of the month, not the middle or beginning
          if (periodIndex === 0) {
            periodTimeStamp = DateTime.fromISO(period.timeStamp).endOf(timeStepWithoutS).toISODate();
          }

          // periodIndex counts down once per in-range period, so both lists have this index.
          const accountPeriod = periodsByAcctId.get(periodIndex)!;
          const balancePeriod = balancePeriods.get(periodIndex)!;
          const total = balancesByDay?.find((day) => day.balanceOn === periodTimeStamp)?.balanceAmount;
          if (typeof total === 'number') {
            if (typeof accountPeriod.total !== 'number' || typeof balancePeriod.total !== 'number') {
              accountPeriod.total = 0;
              balancePeriod.total = 0;
            }

            accountPeriod[String(acc.id)] = total;
            accountPeriod.total += total;
            balancePeriod[normalizedName] = total;
            balancePeriod.total += total;
          } else {
            accountPeriod[String(acc.id)] = null;
            balancePeriod[normalizedName] = null;
          }
          periodIndex -= 1;
        }
      });
    });
    return ImmutableMap<MapOfProps<AccountBalancesResult>>({
      periodsByAcctId,
      periods: balancePeriods,
      txnsByAccountId: retTxnsByAccountId,
      txnItemsByAccountId,
      allTxns,
      allTxnItems,
    });
  },
);

// ================================================================================================================================== //
//                                                      For Display Selectors                                                         //
// ================================================================================================================================== //

export const getIncomeSpendingReportForDisplay = createSelector(
  (state: ReportState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state: ReportState, props: ReportProps) =>
    getReportFilteredTxns(state, {
      ...props,
      reportSpecificFilter: getReportSpecificFilterFromReportName(props.reportName),
    }),
  (state: ReportState) => categoriesSelectors.getCategoriesById(state),
  (state: ReportState) => tagsSelectors.getTags(state),
  (_state: ReportState, props: ReportProps) => props.reportName,
  (_state: ReportState, props: ReportProps) => props.dateRange,
  (_state: ReportState, props: ReportProps) => props.timeStep,
  (_state: ReportState, props: ReportProps) => props.isOverTime,
  (_state: ReportState, props: ReportProps) => props.chartType,
  (_state: ReportState, props: ReportProps) => props.groupBy,
  (_state: ReportState, props: ReportProps) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (_state: ReportState, props: ReportProps) => getReportSpecificFilterFromReportName(props.reportName),
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    dateRange,
    timeStep,
    isOverTime,
    chartType,
    groupBy,
    filterObjectFn,
    reportSpecificFilter,
  ): IncomeSpendingDisplayResult | null => {
    if (loadPending || !txnsList) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'reports',
      {
        txnsList,
        reportName,
        date: dateRange.toISO(),
        timeStep,
        isOverTime,
        chartType,
        groupBy,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'reports',
      (): IncomeSpendingDisplayResult | null => {
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn: TransactionRecord, cbid: CategoriesById) =>
              reportSpecificFilter(txn, cbid) && matchesAppliedFilters(filterObjectFn, txn)
          : reportSpecificFilter;
        const report: ReportDataResult = (isOverTime ? getOverTimeData : getTotalData)(
          txnsList,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        if (!report) return null;
        const retData: Omit<IncomeSpendingDisplayData, 'rawReport'> = {
          allTxns: report.get('allTxns'),
          chartData: {},
          allTxnItems: report.get('allTxnItems'),
        };
        if (chartType === 'bar' || chartType === 'stacked bar' || chartType === 'grouped bar') {
          const signMutliple = reportName === 'total-spending' || reportName === 'business-spending' ? -1 : 1;

          let orderedFields = Object.entries(report.get('allGroups') || {})
            .map(([label, value]) => ({ label, total: value.total, isIgnored: value.isIgnored }))
            .sort((a, b) => signMutliple * b.total - signMutliple * a.total)
            .filter(({ isIgnored }) => !isIgnored)
            .map(({ label }) => label);

          if (orderedFields.length > 4) {
            orderedFields = orderedFields.slice(0, 4).concat(['Everything else']);
          }
          retData.chartData.orderedFields = orderedFields;

          const allZeroGroups: Record<string, number> = {}; // we need to zero out all the groups for each period for "consistency"
          orderedFields.forEach((groupKey) => {
            allZeroGroups[groupKey] = 0;
          });
          retData.total = report.get('total');

          retData.chartData.periods =
            report
              ?.get('periods')
              ?.map((period) => {
                const groupObject: Pick<TransactionChartPeriod, 'txns' | 'txnItems'> &
                  Record<string, ChartPeriod[string]> = period.groups
                  ? { txns: period.txns, txnItems: period.txnItems }
                  : { total: period.total, txns: period.txns, txnItems: period.txnItems };
                if (period.groups) {
                  // handle groups of data
                  Object.entries(period.groups).forEach(([groupKey, groupData]) => {
                    const cappedKey = orderedFields.includes(groupKey) ? groupKey : 'Everything else';
                    const previous = groupObject[cappedKey] || 0;
                    const amount = signMutliple * groupData.total;
                    // Preserve JS addition when a group name collides with a transaction-detail field.
                    groupObject[cappedKey] =
                      typeof previous === 'number' ? previous + amount : String(previous) + amount;
                  });
                }

                return {
                  timeStamp: period.timeStamp,
                  ...allZeroGroups,
                  ...groupObject,
                };
              })
              .toArray() || null;
        } else if (chartType === 'sideways bar' || chartType === 'pie') {
          const convertedGroups = convertAndSortGrouping(
            report?.get('groups') || {},
            reportName === 'total-spending' || reportName === 'business-spending' ? -1 : 1,
          );
          const [cappedGroups, allTxns, allTotal, allTxnItems] = capGrouping(
            convertedGroups,
            9,
            'Everything else',
            true,
          );

          retData.chartData.totals = [
            {
              groupKey: `All ${makeGroupByPlural(groupBy)}`,
              subgroups: cappedGroups,
              total: allTotal,
              txns: allTxns,
              txnItems: allTxnItems,
            },
          ];
          retData.chartData.orderedFields = cappedGroups.map((g) => g.groupKey);
          retData.total = report.get('total');
        }
        return ImmutableMap<IncomeSpendingDisplayData>({ ...retData, rawReport: report });
      },
      cacheKey,
      6,
    );
  },
);

export const getNetIncomeReportForDisplay = createSelector(
  (state: ReportState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state: ReportState, props: ReportProps) =>
    getReportFilteredTxns(state, {
      ...props,
      reportSpecificFilter: getReportSpecificFilterFromReportName('total-spending'),
    }),
  (state: ReportState, props: ReportProps) =>
    getReportFilteredTxns(state, { ...props, reportSpecificFilter: getReportSpecificFilterFromReportName('income') }),
  (state: ReportState) => categoriesSelectors.getCategoriesById(state),
  (state: ReportState) => tagsSelectors.getTags(state),
  (_state: ReportState, props: ReportProps) => props.reportName,
  (_state: ReportState, props: ReportProps) => props.dateRange,
  (_state: ReportState, props: ReportProps) => props.timeStep,
  (_state: ReportState, props: ReportProps) => props.isOverTime,
  (_state: ReportState, props: ReportProps) => props.chartType,
  (_state: ReportState, props: ReportProps) => props.groupBy,
  (_state: ReportState, props: ReportProps) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (
    loadPending,
    spendingTxns,
    incomeTxns,
    categoriesById,
    tagsById,
    reportName,
    dateRange,
    timeStep,
    isOverTime,
    chartType,
    groupBy,
    filterObjectFn,
  ): NetIncomeDisplayResult | null => {
    if (loadPending || !spendingTxns || !incomeTxns) {
      return null;
    }

    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'reports',
      {
        spendingTxns,
        incomeTxns,
        date: dateRange.toISO(),
        timeStep,
        isOverTime,
        groupBy,
        categoriesById,
        tagsById,
        filterObjectFn,
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'reports',
      (): NetIncomeDisplayResult | null => {
        const spendingFilter = getReportSpecificFilterFromReportName('total-spending');
        const spendingFilterIncludingAppliedFilters = filterObjectFn
          ? (txn: TransactionRecord, cbid: CategoriesById) =>
              spendingFilter(txn, cbid) && matchesAppliedFilters(filterObjectFn, txn)
          : spendingFilter;
        const spendingReport: ReportDataResult = (isOverTime ? getOverTimeData : getTotalData)(
          spendingTxns,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          spendingFilterIncludingAppliedFilters,
        );

        const incomeFilter = getReportSpecificFilterFromReportName('income');
        const incomeFilterIncludingAppliedFilters = filterObjectFn
          ? (txn: TransactionRecord, cbid: CategoriesById) =>
              incomeFilter(txn, cbid) && matchesAppliedFilters(filterObjectFn, txn)
          : incomeFilter;
        const incomeReport: ReportDataResult = (isOverTime ? getOverTimeData : getTotalData)(
          incomeTxns,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          incomeFilterIncludingAppliedFilters,
        );

        const allTxns = ImmutableList(
          uniqBy(spendingReport.get('allTxns').concat(incomeReport.get('allTxns')).toArray(), (txn) => txn.id),
        );
        const allTxnItems = ImmutableList(
          spendingReport.get('allTxnItems').concat(incomeReport.get('allTxnItems')).toArray(),
        );
        const periods =
          spendingReport
            .get('periods')
            ?.toArray()
            .map((period, index) => {
              // Both reports are built from the same range and time step, so their periods line up.
              const incomePeriod = incomeReport.get('periods')!.get(index)!;
              const negativeTotal = period.total;
              const positiveTotal = incomePeriod.total;
              const combinedTxns = period.txns.concat(incomePeriod.txns);
              const uniqueCombinedTxns = uniqBy(combinedTxns, (e) => e.id); // eliminate duplicate txns
              const combinedTxnItems = period.txnItems.concat(incomePeriod.txnItems);
              return {
                txns: uniqueCombinedTxns || [],
                txnItems: combinedTxnItems || [],
                negativeTotal: -negativeTotal,
                positiveTotal,
                combinedTotal: positiveTotal + negativeTotal,
                timeStamp: period.timeStamp,
              };
            }) || [];
        const total = incomeReport.get('total') + spendingReport.get('total');
        return ImmutableMap<MapOfProps<NetIncomeDisplayResult>>({
          allTxns,
          allTxnItems,
          chartData: { periods },
          total,
          rawReport: spendingReport,
        });
      },
      cacheKey,
      6,
    );
  },
);

export const getSavingsReportForDisplay = createSelector(
  (state: BalanceReportState) => accountsSelectors.getLoadPending(state) || transactionsSelectors.getLoadPending(state),
  (state: BalanceReportState, props: BalanceReportProps) =>
    getAccountBalances(state, {
      ...props,
      accountsById: accountsSelectors.getSavingAccounts(state),
    }),
  (_state: BalanceReportState, props: BalanceReportProps) => props.chartType,
  (_state: BalanceReportState, props: BalanceReportProps) => props.groupBy,
  (loadPending, report, chartType, groupBy): SavingsDisplayResult | null => {
    if (loadPending || !report) {
      return null;
    }

    const orderedFields =
      !groupBy || groupBy === 'none'
        ? ['total']
        : Object.entries(report.get('periods').last()!) // destructure to entries list
            .filter(
              ([accountName, _value]) =>
                accountName !== 'total' && accountName !== 'timeStamp' && accountName !== 'isoMask',
            ) // remove the non-account properties
            .sort(([_aN1, value1], [_aN2, value2]) => Number(value2) - Number(value1)) // sort based on final month value
            .map(([accountName, _value]) => accountName); // reduce to just account names
    const retData: Omit<SavingsDisplayData, 'rawReport'> = {
      allTxns: report.get('allTxns'),
      allTxnItems: report.get('allTxnItems'),
      chartData: { orderedFields },
      total: report.get('periods').last()?.total,
    };

    if (chartType === 'sideways bar' || chartType === 'pie') {
      const finalMonth = report.get('periods').last()!;
      const groups = retData.chartData.orderedFields.map((accountName) => ({
        groupKey: accountName,
        subgroups: null,
        total: Number(finalMonth[accountName]),
        txns: groupBy === 'none' ? report.get('allTxns') : report.get('txnsByAccountId')[accountName],
        txnItems: groupBy === 'none' ? report.get('allTxnItems') : report.get('txnItemsByAccountId')[accountName],
      }));

      const convertedGroups = convertAndSortGrouping(groups || {}, 1);
      const [cappedGroups, allTxns, allTotal, allTxnItems] = capGrouping(convertedGroups, 9, 'Everything else');

      retData.chartData.totals = [
        { groupKey: 'All Savings', subgroups: cappedGroups, total: allTotal, txns: allTxns, txnItems: allTxnItems },
      ];
    } else {
      retData.chartData.periods = report.get('periods').toArray();
    }

    return ImmutableMap<SavingsDisplayData>({ ...retData, rawReport: report });
  },
);

const getAccountsInPeriods = (
  periodsByAcctId: List<BalancePeriod>,
  accountsById: ImmutableMap<string, Account>,
): ImmutableMap<string, Account> =>
  accountsById.filter((acc) => periodsByAcctId.some((date) => typeof date[String(acc.id)] === 'number'));

const getNetWorthTableData = (
  periods: BalancePeriod[],
  accountsById: ImmutableMap<string, Account>,
  timeStep: string | undefined,
  filterObject: BalanceReportProps['filterObject'],
  reportableAccounts: ImmutableMap<string, Account>,
): NetWorthReportData => {
  let formatString = 'MMM yyyy';
  if (timeStep === 'days') {
    formatString = 'MMM dd';
  } else if (timeStep === 'years') {
    formatString = 'yyyy';
  }

  const dateList = periods.map((date) => DateTime.fromISO(date.timeStamp).toFormat(formatString));

  const filteredAccounts = filterAccounts(accountsById, reportableAccounts, filterObject);

  const reformattedAccounts = filteredAccounts.map((account) => {
    const selectedData: NetWorthAccount['data'] = {};
    periods.forEach((day) => {
      const amount = day[String(account.id)];
      const formattedDate = DateTime.fromISO(day.timeStamp).toFormat(formatString);
      // Account-ID entries hold balances; the string entries are date metadata.
      selectedData[formattedDate] = amount as number | null | undefined;
    });

    if (Object.keys(selectedData).length !== 0) {
      return {
        accountName: account?.name,
        data: selectedData,
        type: getL2GroupingData(null, account),
      };
    }
    return null;
  });

  let numberOfDebtTypes = 0;
  let numberOfAssetTypes = 0;

  const zeroValueDates: HeaderData = {};

  dateList.forEach((date) => {
    zeroValueDates[date] = null;
  });

  const assets = {
    displayLabel: 'Assets',
    headerData: { ...zeroValueDates },
    subData: {} as Record<string, SubDataItem>,
  };

  const debt = {
    displayLabel: 'Debt',
    headerData: { ...zeroValueDates },
    subData: {} as Record<string, SubDataItem>,
  };

  const netWorth = {
    headerData: { ...zeroValueDates },
    displayLabel: 'Net Worth',
  };

  reformattedAccounts.forEach((account) => {
    if (account) {
      const typeId = account.type.id;
      const { displayLabel } = account.type;
      // debts go first because 'OTHER' (catchall) exists in assets and can match OTHER_LIABILITY
      if (nodeDebtIds.some((id) => typeId.includes(id))) {
        if (typeId && displayLabel && !debt.subData[typeId]) {
          numberOfDebtTypes += 1;
          debt.subData[typeId] = {
            displayLabel,
            accounts: [],
            headerData: { ...zeroValueDates },
          };
        }
        dateList.forEach((date) => {
          if (typeof account.data[date] === 'number') {
            // We now know the acct isn't null, by default it is null, so we must change it to 0 so it can be summed
            if (typeof debt.headerData[date] !== 'number') {
              debt.headerData[date] = 0;
            }
            if (typeof debt.subData[typeId].headerData[date] !== 'number') {
              debt.subData[typeId].headerData[date] = 0;
            }
            if (typeof netWorth.headerData[date] !== 'number') {
              netWorth.headerData[date] = 0;
            }

            debt.headerData[date] += account.data[date];
            debt.subData[typeId].headerData[date] += account.data[date];
            netWorth.headerData[date] += account.data[date];
          }
        });
        debt.subData[typeId].accounts.push(account);
      } else if (nodeAssetIds.some((id) => typeId.includes(id))) {
        if (typeId && displayLabel && !assets.subData[typeId]) {
          numberOfAssetTypes += 1;
          assets.subData[typeId] = {
            displayLabel,
            accounts: [],
            headerData: { ...zeroValueDates },
          };
        }
        dateList.forEach((date) => {
          if (typeof account.data[date] === 'number') {
            // We now know the acct isn't null, by default it is null, so we must change it to 0 so it can be summed
            if (typeof assets.headerData[date] !== 'number') {
              assets.headerData[date] = 0;
            }
            if (typeof assets.subData[typeId].headerData[date] !== 'number') {
              assets.subData[typeId].headerData[date] = 0;
            }
            if (typeof netWorth.headerData[date] !== 'number') {
              netWorth.headerData[date] = 0;
            }

            assets.headerData[date] += account.data[date];
            assets.subData[typeId].headerData[date] += account.data[date];
            netWorth.headerData[date] += account.data[date];
          }
        });
        assets.subData[typeId].accounts.push(account);
      }
    }
  });
  const assetSubData = Object.entries(assets.subData).sort(([aId, _a], [bId, _b]) => {
    const matchingNodeIdForA = nodeAssetIds.find((nodeId) => aId.includes(nodeId));
    const matchingNodeIdForB = nodeAssetIds.find((nodeId) => bId.includes(nodeId));
    return nodeAssetIds.indexOf(matchingNodeIdForA!) - nodeAssetIds.indexOf(matchingNodeIdForB!);
  });

  const debtSubData = Object.entries(debt.subData).sort(([aId, _a], [bId, _b]) => {
    const matchingNodeIdForA = nodeDebtIds.find((nodeId) => aId.includes(nodeId));
    const matchingNodeIdForB = nodeDebtIds.find((nodeId) => bId.includes(nodeId));
    return nodeDebtIds.indexOf(matchingNodeIdForA!) - nodeDebtIds.indexOf(matchingNodeIdForB!);
  });
  return {
    numberOfDebtTypes,
    numberOfAssetTypes,
    dateList,
    netWorth,
    assets: { ...assets, subData: assetSubData },
    debt: { ...debt, subData: debtSubData },
  };
};

export const getNetWorthReportForDisplay = createSelector(
  (state: BalanceReportState) => accountsSelectors.getLoadPending(state) || transactionsSelectors.getLoadPending(state),
  (state: BalanceReportState, props: BalanceReportProps) =>
    getAccountBalances(state, { ...props, accountsById: accountsSelectors.getAccountsById(state) }),
  (state: BalanceReportState) => accountsSelectors.getAccountsById(state),
  (_state: BalanceReportState, props: BalanceReportProps) => props.groupBy,
  (_state: BalanceReportState, props: BalanceReportProps) => props.timeStep,
  (_state: BalanceReportState, props: BalanceReportProps) => props.filterObject,
  (state: BalanceReportState) => accountsSelectors.getAccountsById(state),
  (
    loadPending,
    report,
    accountsById,
    groupBy,
    timeStep,
    filterObject,
    reportableAccounts,
  ): NetWorthDisplayResult | null => {
    if (loadPending || !report) {
      return null;
    }

    const allTxns = report.get('allTxns');
    const periods = report.get('periods').toArray();
    const orderedFields =
      groupBy === 'account'
        ? getAccountsInPeriods(report.get('periodsByAcctId'), accountsById)
            .map((acc) => acc.name)
            .toList()
            .toArray()
        : null;
    const total = periods[periods.length - 1]?.total;
    const tableData = getNetWorthTableData(
      report.get('periodsByAcctId').toArray(),
      accountsById,
      timeStep,
      filterObject,
      reportableAccounts,
    );

    return ImmutableMap<MapOfProps<NetWorthDisplayResult>>({
      allTxns,
      chartData: { periods, orderedFields },
      total,
      tableData,
      rawReport: report,
    });
  },
);

type ReportDisplayProps = ReportProps & {
  pageInitialized?: boolean;
  subSelector?: ((state: BalanceReportState, props: ReportProps) => DisplayReportResult | null) | null;
};

// wrapper to combine all above selectors into one call
export const getReportForDisplay = createSelector(
  (state: BalanceReportState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state: BalanceReportState, props: ReportDisplayProps) =>
    (props.pageInitialized && props.subSelector && props.subSelector(state, props)) || null,
  (loadPending, focusedReport): DisplayReportResult | null => {
    if (!loadPending && focusedReport) {
      return focusedReport;
    }
    return null;
  },
);

const getLastMonthInterval = (): Interval => constructPredefinedInterval({ value: 'LAST_MONTH' }, DateTime.local());
const getLast2MonthsInterval = (): Interval =>
  constructPredefinedInterval(
    {
      value: 'CUSTOM',
      startDate: getStartOfMonthFromThisMonth(DateTime.local(), -2),
      endDate: getStartOfMonthFromThisMonth(DateTime.local(), 0),
    },
    DateTime.local(),
  );
const NO_BILLS_OR_SUBSCRIPTIONS_FILTER = (txn: TransactionRecord): boolean => !txn.isBill && !txn.isSubscription;

// returns goals by id with the extra value, contributedLastMonth
const getGoalsDataForLastMonth = createSelector(
  (state: MonthlySummaryState) => transactionsSelectors.getTransactionsByAccountId(state),
  (state: MonthlySummaryState) => goalsSelectors.getGoalsStore(state).resourcesById,
  (txnsByAccountId, goalsById): ImmutableMap<string, MonthlyGoal> => {
    const lastMonthInterval = getLastMonthInterval();

    const txnsByAccountIdFromLastMonthOnly = txnsByAccountId.map((txns) =>
      txns.filter((t) => t && intervalContainsInclusive(lastMonthInterval, DateTime.fromISO(t.postedOn))),
    );

    const incompleteGoalsStartedBeforeThisMonth = goalsById.filter(
      (goal) => !goal.get('completedOn') && DateTime.fromISO(goal.get('startOn')!) < DateTime.local().startOf('month'),
    );

    return incompleteGoalsStartedBeforeThisMonth.map((goal) => {
      const txns = txnsByAccountIdFromLastMonthOnly.get(String(goal.accountId));
      const contributionsLastMonth = txns?.reduce((accumulator, val) => accumulator + val.amount, 0) || 0;
      return {
        ...goal.toJS(),
        // txns,
        contributionsLastMonth,
        percentFinished: Math.min(1, contributionsLastMonth / Number(goal.get('contributionAmount'))),
      };
    });
  },
);

export const getMonthlySummary = createSelector(
  (state: MonthlySummaryState) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    goalsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (state: MonthlySummaryState, props: ReportFilterProps = {}) =>
    getReportFilteredTxns(state, {
      ...props,
      dateRange: getLast2MonthsInterval(),
      reportSpecificFilter: getReportSpecificFilterFromReportName('total-spending'),
    }),
  (state: MonthlySummaryState, props: ReportFilterProps = {}) =>
    getReportFilteredTxns(state, {
      ...props,
      dateRange: getLast2MonthsInterval(),
      reportSpecificFilter: getReportSpecificFilterFromReportName('income'),
    }),
  (state: MonthlySummaryState) => getGoalsDataForLastMonth(state),
  (
    loadPending,
    categoriesById,
    tagsById,
    spendingTxns,
    incomeTxns,
    lastMonthGoalsData,
  ): MapOf<MonthlySummaryData> | null => {
    if (loadPending || !spendingTxns || !incomeTxns) return null;

    const LAST_2_MONTHS_INTERVAL = getLast2MonthsInterval();

    const spendingNoBillsFilter = (txn: TransactionRecord): boolean | undefined =>
      getReportSpecificFilterFromReportName('total-spending')(txn, categoriesById) &&
      NO_BILLS_OR_SUBSCRIPTIONS_FILTER(txn);

    const spendingByNone = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'none',
      categoriesById,
      tagsById,
      getReportSpecificFilterFromReportName('total-spending'),
    );
    const spendingByCategory = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'category',
      categoriesById,
      tagsById,
      spendingNoBillsFilter,
    );
    const spendingByPayee = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'payee',
      categoriesById,
      tagsById,
      spendingNoBillsFilter,
    );

    const incomeByNone = getOverTimeData(
      incomeTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'none',
      categoriesById,
      tagsById,
      getReportSpecificFilterFromReportName('income'),
    );

    const lastMonthIncomeTotal = incomeByNone.get('periods').get(1)!.total;
    const monthBeforeLastIncomeTotal = incomeByNone.get('periods').get(0)!.total;
    const lastMonthSpendingTotal = spendingByNone.get('periods').get(1)!.total;
    const monthBeforeLastSpendingTotal = spendingByNone.get('periods').get(0)!.total;

    const lastMonthExpensesOnBills =
      spendingByNone
        .get('periods')
        .get(1)!
        .txns?.reduce(
          (accumulator, val) => (val.isBill || val.isSubscription ? accumulator + val.amount : accumulator),
          0,
        ) || 0;
    const monthBeforeLastExpensesOnBills =
      spendingByNone
        .get('periods')
        .get(0)!
        .txns?.reduce(
          (accumulator, val) => (val.isBill || val.isSubscription ? accumulator + val.amount : accumulator),
          0,
        ) || 0;

    let percentDirection = lastMonthIncomeTotal > monthBeforeLastIncomeTotal ? 1 : -1;
    const percentChangeIncome =
      Math.sign(monthBeforeLastIncomeTotal) !== 0
        ? // division by 0 check
          (percentDirection * 100 * Math.abs(lastMonthIncomeTotal - monthBeforeLastIncomeTotal)) /
          Math.abs(monthBeforeLastIncomeTotal)
        : 100;
    percentDirection = lastMonthSpendingTotal < monthBeforeLastSpendingTotal ? 1 : -1; // more negative means more spending
    const percentChangeSpending =
      Math.sign(monthBeforeLastSpendingTotal) !== 0
        ? // division by 0 check
          (percentDirection * 100 * Math.abs(lastMonthSpendingTotal - monthBeforeLastSpendingTotal)) /
          Math.abs(monthBeforeLastSpendingTotal) // -1 to flip spending amounts to calculate change
        : 100;

    const orderedSpendingByCategory = ImmutableList(
      Object.entries(spendingByCategory.get('periods').get(1)!.groups!)
        .map(([cat, val]) => {
          const lastMonthTotal = val.total || 0;
          const monthBeforeTotal =
            Object.entries(spendingByCategory.get('periods').get(0)!.groups!).find(
              ([itemName, _]) => itemName === cat,
            )?.[1].total || 0;
          const percentDirec = lastMonthTotal < monthBeforeTotal ? 1 : -1; // more negative means more spending
          const percentChange =
            monthBeforeTotal !== 0
              ? (Math.abs(lastMonthTotal - monthBeforeTotal) / Math.abs(monthBeforeTotal)) * 100 * percentDirec
              : 100;
          return {
            coaName: cat,
            lastMonthTotal,
            monthBeforeTotal,
            percentChange,
          };
        })
        .filter((item) => item.lastMonthTotal !== 0)
        .sort((first, second) => first.lastMonthTotal - second.lastMonthTotal),
    );

    const orderedSpendingByPayee = Object.entries(spendingByPayee.get('periods').get(1)!.groups!)
      .map(([payee, val]) => {
        const lastMonthTotal = val.total || 0;
        const monthBeforeTotal =
          Object.entries(spendingByPayee.get('periods').get(0)!.groups!).find(
            ([itemName, _]) => itemName === payee,
          )?.[1].total || 0;
        const percentDirec = lastMonthTotal < monthBeforeTotal ? 1 : -1; // more negative means more spending
        const percentChange =
          monthBeforeTotal !== 0
            ? (Math.abs(lastMonthTotal - monthBeforeTotal) / Math.abs(monthBeforeTotal)) * 100 * percentDirec
            : 100;
        return {
          payeeName: payee,
          lastMonthTotal,
          monthBeforeTotal,
          percentChange,
          numTxns: val?.txns?.length || 0,
        };
      })
      .filter((item) => item.lastMonthTotal !== 0)
      .sort((first, second) => first.lastMonthTotal - second.lastMonthTotal);

    const lastMonthNetIncomeTotal = lastMonthIncomeTotal + lastMonthSpendingTotal;
    const monthBeforeLastNetIncomeTotal = monthBeforeLastIncomeTotal + monthBeforeLastSpendingTotal;
    percentDirection = lastMonthNetIncomeTotal > monthBeforeLastNetIncomeTotal ? 1 : -1;
    let percentChangeNetIncome =
      Math.round(percentDirection * 100 * Math.abs(lastMonthNetIncomeTotal - monthBeforeLastNetIncomeTotal)) /
      Math.abs(monthBeforeLastNetIncomeTotal);
    if (!Number.isFinite(percentChangeNetIncome)) {
      percentChangeNetIncome = percentChangeNetIncome > 0 ? 100 : -100;
    }

    return ImmutableMap<MonthlySummaryData>({
      lastMonthIncomeTotal,
      monthBeforeLastIncomeTotal,
      lastMonthSpendingTotal,
      monthBeforeLastSpendingTotal,
      lastMonthExpensesOnBills,
      monthBeforeLastExpensesOnBills,
      percentChangeIncome,
      percentChangeSpending,
      orderedSpendingByCategory,
      orderedSpendingByPayee,
      lastMonthGoalsData,
      lastMonthNetIncomeTotal,
      percentChangeNetIncome,
    });
  },
);
